// Feature flags gate which top-level features a user can see and use.
// The flag keys MUST match the backend (app/core/auth.py KNOWN_FEATURE_FLAGS) and
// the Snowflake USERS.FEATURE_FLAGS array values.

export type FeatureFlag = 'main' | 'client_view' | 'broker_view' | 'broker_admin' | 'rds_mia_agent' | 'sales_mia_agent' | 'sales_view';

export interface FeatureDef {
  key: FeatureFlag;
  label: string;
  description: string;
}

export const FEATURE_DEFS: FeatureDef[] = [
  { key: 'main', label: 'Main', description: 'Main inventory dashboard' },
  { key: 'client_view', label: 'Client View', description: 'Client View dashboard & map' },
  { key: 'broker_view', label: 'Broker View', description: 'Broker View dashboard & map' },
  { key: 'broker_admin', label: 'Broker Admin', description: 'Edit global Broker settings (fit weights, urgency, exclusions)' },
  { key: 'rds_mia_agent', label: 'Mia', description: 'AI assistant inside Client View' },
  { key: 'sales_mia_agent', label: 'Mia (Sales)', description: 'AI assistant inside Sales View' },
  { key: 'sales_view', label: 'Sales View', description: 'Sales View dashboard & map for CSE / Dealer Sales' },
];

export const ALL_FEATURE_KEYS: FeatureFlag[] = FEATURE_DEFS.map((f) => f.key);

// Flags a user does NOT inherit when their flags are unconfigured. Broker
// settings are global and saving them kicks a recompute, so broker_admin has to
// be granted explicitly. sales_view is the ENTIRE access gate for Sales View --
// territory only seeds default filters -- so an RDS user inheriting it would
// land on the whole CSE book, not an empty one. sales_mia_agent follows
// sales_view: an agent for a surface the user cannot reach is not a feature.
// MUST match backend NON_DEFAULT_FEATURE_FLAGS.
export const NON_DEFAULT_FEATURE_KEYS: FeatureFlag[] = ['broker_admin', 'sales_mia_agent', 'sales_view'];
export const DEFAULT_FEATURE_KEYS: FeatureFlag[] = ALL_FEATURE_KEYS.filter(
  (k) => !NON_DEFAULT_FEATURE_KEYS.includes(k),
);

/**
 * Whether a user has access to a feature.
 *
 * The backend resolves legacy users to a non-empty list, while Entra users with
 * no assigned views can arrive here with an empty array. We treat undefined as
 * "not configured" (fail open for legacy compatibility), but an explicit empty
 * array means "authenticated but no access".
 */
export function hasFeature(flags: string[] | undefined, key: FeatureFlag): boolean {
  if (flags === undefined) return DEFAULT_FEATURE_KEYS.includes(key);
  if (flags.length === 0) return false;
  return flags.includes(key);
}

/**
 * Whether a user may USE a feature: admin OR the flag.
 *
 * The one rule every gate should ask, because every backend guard already works this way
 * (`_require_agent`, `_require_sales_view`, `_require_broker_admin` all let an admin through).
 * `hasFeature` alone hid a feature the API would happily serve — which is exactly what happened
 * to Mia: an admin whose flags did not list `sales_mia_agent` (a NON-DEFAULT flag, so most of
 * them) got a working endpoint and no widget.
 */
export function canUseFeature(
  isAdmin: boolean | undefined,
  flags: string[] | undefined,
  key: FeatureFlag,
): boolean {
  return Boolean(isAdmin) || hasFeature(flags, key);
}
